import { RouterModule } from "@angular/router";

import { Component, inject, OnInit } from '@angular/core'
import { ColDef } from 'ag-grid-community'
import { GridApi, GridState, RowClassRules, SideBarDef } from 'ag-grid-enterprise'
import { GridReadyEvent } from 'ag-grid-community'
import { ArticleTemplateService } from '../../_services'
import { AG_GRID_LOCALE_DE } from 'src/app/_helpers/aggrid.locale.de'
import { AgGridGenericLinkRenderer } from '../_shared/ag-grid-renderer/ag-grid-generic-link.component'
import { AgGridAngular } from "ag-grid-angular";
import { AgGridThemeService } from "src/app/_services/ag-grid-theme.service";
import { CdkMenu, CdkMenuTrigger } from "@angular/cdk/menu";

@Component({
  imports: [RouterModule, AgGridAngular, CdkMenu, CdkMenuTrigger],
  selector: 'hawk-article-templates',
  templateUrl: './article-templates.component.html',
  styleUrls: ['./article-templates.component.scss'],
  host: {
    class: 'app-page',
  }
})
export class ArticleTemplatesComponent implements OnInit {
  public hawkTheme = inject(AgGridThemeService).getTheme();
  // For now lets go without a model. Checking with signkey
  articleTemplates: any[]
  public locale = AG_GRID_LOCALE_DE
  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ['columns', 'filters']
  }
  public paginationPageSize = 50;
  public paginationPageSizeSelector = [50,100,300,1000,9000]
  public storageKey = 'articleTemplateSearchStateSettings'

  public rowSelection: "single" | "multiple" = "multiple"
  public initialState?: GridState;
  // public rowModelType = 'serverSide';
  public rowClassRules: RowClassRules = {
    // if tech on site, color row
    'tech-on-site': (params) => {
      if (params?.data === undefined) return "...";
      return params.data.tech_on_site
    },
  }

  public defaultColDef: ColDef = {
    filter: true,
    flex: 1
  }

  private boolRenderer = (params: any) => params.value ? '<span class="fa fa-check"></span>' : '-'
  private currencyRenderer = (params: any) => params.value > 0 ? `${params.value} €` : '-'
  private dashIfEmpty = (params: any) => params.value || '-'

  // Column Definitions: Defines the columns to be displayed.
  public colDefs: ColDef[] = [
    {
      field: "id",
      headerName: 'ID',
      minWidth: 70,
      width: 70,
      maxWidth: 100
    },
    {
      field: "active",
      headerName: 'Aktiv',
      cellRenderer: this.boolRenderer,
      width: 80,
      maxWidth: 100,
    },
    {
      field: "description",
      headerName: 'Bezeichnung',
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        labelFromData: true,
        dataKey: 'description',
        routerLink: ['/', 'artikelvorlagen', '@id']
      },
      filter: "agTextColumnFilter",
    },
    {
      field: "product_category",
      headerName: 'Produktkategorie',
      filter: "agTextColumnFilter",
    },
    {
      field: "vendor",
      headerName: 'Hersteller',
      filter: "agTextColumnFilter",
    },
    {
      field: "part_nr",
      headerName: 'Part-Nr',
      filter: "agTextColumnFilter",
    },
    {
      field: "end_of_life",
      headerName: 'End of Life',
      cellRenderer: this.boolRenderer,
      width: 100,
      maxWidth: 120,
    },
    {
      field: "sn",
      headerName: 'Mit SN',
      cellRenderer: this.boolRenderer,
      width: 80,
      maxWidth: 100,
    },
    {
      field: "comment1",
      headerName: 'Kommentar 1',
      filter: "agTextColumnFilter",
    },
    {
      field: "comment2",
      headerName: 'ProjektID Zugehörigkeit',
      filter: "agTextColumnFilter",
    },
    {
      field: "comment3",
      headerName: 'Kunden-ID',
      filter: "agTextColumnFilter",
    },
    {
      field: "default_owner_bentomax",
      headerName: 'Eigentümer BM',
      cellRenderer: this.boolRenderer,
      width: 120,
      maxWidth: 140,
    },
    {
      field: "bad_part_handling",
      headerName: 'Bad Part',
      cellRenderer: this.dashIfEmpty,
      filter: "agTextColumnFilter",
    },
    {
      field: "info",
      headerName: 'Info',
      filter: "agTextColumnFilter",
      tooltipField: 'info',
    },
    {
      field: "articles_count",
      headerName: 'Bestand',
      cellRenderer: (params: any) => (`${params.data.articles_count > 0 ? params.data.articles_count : '-' }`)
    },
    {
      field: "warning_threshold",
      headerName: 'Min (Warn)',
      cellRenderer: (params: any) => {
        const minimum_stock = params.data.minimum_stock;
        const warning_threshold = params.data.warning_threshold;
        return `${minimum_stock > 0 ? minimum_stock : '-' }(${warning_threshold > 0 ? warning_threshold : '-'})`
      }
    },
    {
      field: "purchase_value",
      headerName: 'Einkauf',
      cellRenderer: this.currencyRenderer,
      width: 100,
    },
    {
      field: "selling_value",
      headerName: 'Verkauf',
      cellRenderer: this.currencyRenderer,
      width: 100,
      hide: true,
    },
    {
      field: "package_height",
      headerName: 'Paket (H×B×L / Gewicht)',
      cellRenderer: (params: any) => {
        const h = params.data.package_height
        const w = params.data.package_width
        const l = params.data.package_length
        const g = params.data.package_weight
        if (!h && !w && !l && !g) return '-'
        const dim = [h, w, l].map(v => v ?? '-').join(' × ')
        return `${dim} cm / ${g ?? '-'} g`
      },
      tooltipValueGetter: (params: any) => {
        const d = params.data
        if (!d) return ''
        return `Höhe: ${d.package_height ?? '-'} cm, Breite: ${d.package_width ?? '-'} cm, Länge: ${d.package_length ?? '-'} cm, Gewicht: ${d.package_weight ?? '-'} g`
      },
      width: 220,
      filter: false,
    },
    {
      field: "order_number",
      headerName: 'Bestellnummer',
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "order_link",
      headerName: 'Bestelllink',
      cellRenderer: (params: any) => {
        if (!params.value) return '-'
        const short = params.value.length > 30 ? params.value.slice(0, 30) + '...' : params.value
        return `<a href="${params.value}" target="_blank" rel="noopener" class="hover_link">${short}</a>`
      },
      tooltipField: 'order_link',
      hide: true,
    },
    {
      field: "unidata_report",
      headerName: 'UniData Bericht',
      cellRenderer: this.boolRenderer,
      width: 120,
      hide: true,
    },
    {
      field: "unidatahdd",
      headerName: 'UniData HDD',
      cellRenderer: this.boolRenderer,
      hide: true,
      width: 110,
    },
    {
      field: "unidata_reinstall",
      headerName: 'UniData Reinstall',
      cellRenderer: this.boolRenderer,
      width: 130,
      hide: true,
    },
  ]

  private gridApi!: GridApi;

  constructor(
    private articleTemplateService: ArticleTemplateService
  ) { }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  public quicksearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.gridApi.setGridOption(
      "quickFilterText",
      newValue,
    )
  }

  ngOnInit(): void {
    this.initialState = this.getState()

    this.articleTemplateService.all({ detail_data: true }).subscribe({
      next: (data) => {
        this.articleTemplates = data;
      },
      error: (err) => {
        console.log('err', err);
      }
    })
  }

  public saveState() {
    const state = this.gridApi.getState()
    delete state['filter']
    delete state.pagination.page
    const serializedState = JSON.stringify(state)
    localStorage.setItem(this.storageKey, serializedState)
  }

  public deleteState() {
    localStorage.removeItem(this.storageKey)
  }

  public getState(): GridState {
    return JSON.parse(localStorage.getItem(this.storageKey))
  }

}